import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Search, Loader2, MessageCircle, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppDeeplinkButton from '../components/WhatsAppDeeplinkButton';
import { apiFetchJson } from '../lib/api';
import { formatPrice, normalizePhone } from '../lib/utils';
import { trackPageView } from '../lib/analytics';
import type { Order } from '../types';

const STATUS_LABELS: Record<string, string> = {
  pending_whatsapp: '等待 WhatsApp 確認',
  pending_payment: '等待付款',
  pending_confirmation: '等待確認付款',
  payment_confirmed: '已確認付款',
  processing: '備貨中',
  shipped: '已發貨',
  completed: '已完成',
  cancelled: '已取消',
};

const STATUS_COLORS: Record<string, string> = {
  pending_whatsapp: 'bg-amber-100 text-amber-700',
  pending_payment: 'bg-blue-100 text-blue-700',
  pending_confirmation: 'bg-cyan-100 text-cyan-700',
  payment_confirmed: 'bg-green-100 text-green-700',
  processing: 'bg-purple-100 text-purple-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-stone-200 text-stone-600',
};

export default function OrderQuery() {
  const { orderNum: paramOrderNum } = useParams<{ orderNum?: string }>();
  const [searchParams] = useSearchParams();
  const [orderNum, setOrderNum] = useState(paramOrderNum || '');
  const [phone, setPhone] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    trackPageView('/order/:orderNum');
    const queryOrder = searchParams.get('order');
    if (queryOrder) setOrderNum(queryOrder);
  }, [searchParams]);

  const handleQuery = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!orderNum || !phone) return;

    setLoading(true);
    setError('');

    const params = new URLSearchParams({ phone: normalizePhone(phone) });
    const res = await apiFetchJson<Order>(`/public/orders/${orderNum}/status?${params.toString()}`);

    setLoading(false);
    if (res.success && res.data) {
      setOrder(res.data);
    } else {
      setError(res.error || '查詢失敗，請檢查訂單編號及電話');
      setOrder(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-x1493-dark text-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold">訂單查詢</h1>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-xl mx-auto px-4">
          <form onSubmit={handleQuery} className="bg-stone-50 rounded-2xl p-6 space-y-4 mb-8">
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-1">訂單編號</label>
              <input
                type="text"
                value={orderNum}
                onChange={(e) => setOrderNum(e.target.value)}
                placeholder="例如 AB1234"
                className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-1">下單電話</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="例如 9123 4567"
                className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !orderNum || !phone}
              className="w-full bg-x1493-primary text-white font-bold py-3 rounded-xl hover:bg-red-700 disabled:opacity-60 transition flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
              查詢訂單
            </button>
          </form>

          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-xl mb-8 flex items-center gap-2">
              <AlertCircle size={20} />
              {error}
            </div>
          )}

          {order && (
            <div className="bg-stone-50 rounded-2xl p-6 space-y-4">
              <div className="flex justify-between items-center pb-4 border-b border-stone-200">
                <span className="text-stone-600">訂單編號</span>
                <span className="font-bold text-xl">#{order.orderNum}</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-stone-200">
                <span className="text-stone-600">總金額</span>
                <span className="font-bold text-xl text-x1493-primary">{formatPrice(order.finalPrice)}</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-stone-200">
                <span className="text-stone-600">狀態</span>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${STATUS_COLORS[order.status] || 'bg-stone-100'}`}>
                  {STATUS_LABELS[order.status] || order.status}
                </span>
              </div>

              {order.status === 'pending_whatsapp' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-4">
                  <div className="flex items-start gap-2 text-amber-800">
                    <MessageCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm">訂單尚未完成 WhatsApp 確認。點擊下方按鈕發送確認訊息。</p>
                  </div>
                  <WhatsAppDeeplinkButton orderNum={order.orderNum} variant="large" />
                </div>
              )}

              {order.status === 'pending_payment' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-blue-800 font-bold mb-2">請付款並上傳憑證</p>
                  <a
                    href={`/payment-proof/${order.orderNum}`}
                    className="inline-block bg-blue-600 text-white font-bold px-6 py-2 rounded-xl hover:bg-blue-700 transition"
                  >
                    上傳付款憑證
                  </a>
                </div>
              )}

              {order.status === 'pending_confirmation' && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
                  <p className="text-cyan-800 font-bold">已收到付款憑證，等待管理員確認</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
